<template>
	<div>
	<!-- Шапка — общий стандарт (SectionPageTitle), период — в её слоте. -->
	<SectionPageTitle title="Overview" icon="home">
		<Dropdown align="right" class="pos-relative z-index-3">
			<template #label>
				<div class="flex flex-v-center gap-thin bg-card pd-thin pd-l-small pd-r-small radius-small cursor-pointer">
					<Icon name="calendar" class="i-small" />
					<span class="t-small">{{ dateRangeLabel }}</span>
				</div>
			</template>
			<Calendar
				v-model:date="dateRange"
				:allowRange="true"
				class="bg-base radius-small pd-small"
			/>
		</Dropdown>
	</SectionPageTitle>
	<div class="pd-medium">
		<!-- 1. KPI Cards -->
		<Block title="Statistics" class="mn-b-medium">
			<div class="flex gap-micro mobile:flex-column">
				<CardStat
					v-if="isModuleInstalled('auth')"
					title="Active Users"
					:value="userStats.active?.toLocaleString() || '—'"
					class="w-min-8r"
				/>

				<CardStat
					v-if="isModuleInstalled('orders')"
					title="Total Orders"
					:value="orderStats.total?.orderCount?.toLocaleString() || '—'"
					class="w-min-8r"
				/>

				<CardStat
					v-if="isModuleInstalled('orders')"
					title="Total Revenue"
					:value="'₽' + (orderStats.total?.totalRevenue?.toLocaleString() || '0')"
					class="w-min-8r"
				/>
			</div>
		</Block>

		<!-- 2. Revenue Chart — FULL WIDTH -->
		<Block v-if="isModuleInstalled('orders')" title="Revenue" class="mn-b-medium">
			<Chart
				:data="revenueChart"
				valueKey="revenue"
				labelKey="_id"
				:formatValue="formatChartValue"
				:formatLabel="formatXDate"
				emptyText="No revenue data"
			>
				<template #tooltip="{ item }">
					₽{{ item.revenue.toLocaleString() }} · {{ formatXDate(item._id) }}
				</template>
			</Chart>
		</Block>

		<!-- 3. Recent Orders + 4. Upcoming Events -->
		<div v-if="isModuleInstalled('orders') || isModuleInstalled('events')" class="mn-b-medium cols-2 mobile:cols-1 gap-small">
			<Block v-if="isModuleInstalled('orders')" title="Recent Orders">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:states="{ empty: { title: 'No Orders', description: 'No orders yet.' } }"
					:store="{ read: (options) => orders.actions.read(options), state: orders.state }"
					:options="{ limit: 5, page: 1, sortParam: 'createdAt', sortOrder: 'desc', dateStart: dateRange.start, dateEnd: dateRange.end }"
					v-slot="{ items }"
				>
					<Table
						class="radius-small bg-base"
						:items="items"
						:columns="orderColumns"
						:rowLink="(row) => `/backoffice/orders/${row._id}`"
					>
						<template #cell-_id="{ value }">
							<span class="t-secondary fw-medium">#{{ formatId(value) }}</span>
						</template>
						<template #cell-positions="{ value }">
							<span class="t-secondary">{{ value?.length || 0 }} items</span>
						</template>
						<template #cell-status="{ value }">
							<span
								class="pd-thin pd-l-small pd-r-small radius-small t-small fw-medium"
								:class="getStatusClass(value)"
							>
								{{ value }}
							</span>
						</template>
						<template #cell-createdAt="{ value }">
							<span class="t-secondary t-small">{{ formatDate(value, { dateOnly: true }) }}</span>
						</template>
					</Table>
				</Feed>
			</Block>

			<Block v-if="isModuleInstalled('events')" title="Upcoming Events">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:states="{ empty: { title: 'No Events', description: 'No upcoming events.' } }"
					:store="{ read: (options) => events.read(options), state: events.state }"
					:options="{ sortParam: 'date.start', sortOrder: 'desc', limit: 5, page: 1 }"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="event in items"
						:key="event._id"
						:to="`/events/${event.url || event._id}`"
						:photo="event.cover"
						placeholder="events"
						:title="event.name"
						:subtitle="formatDate(event.date?.start, { dateOnly: true })"
						classImage="flex-none w-3r h-3r radius-small bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					>
						<template #actions>
							<span
								class="flex-none pd-thin pd-l-small pd-r-small radius-small t-small fw-medium"
								:class="getEventStatusClass(event.date)"
							>
								{{ getEventStatus(event.date) }}
							</span>
						</template>
					</Card>
				</Feed>
			</Block>
		</div>

		<!-- 5. Recent Applications + 6. Recent Customers -->
		<div v-if="isModuleInstalled('orders')" class="mn-b-medium cols-2 mobile:cols-1 gap-small">
			<Block title="Recent Applications">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:states="{ empty: { title: 'No Applications', description: 'No applications.' } }"
					:store="{ read: (options) => applications.read(options), state: applications.state }"
					:options="{ sortParam: 'createdAt', sortOrder: 'desc', limit: 5, page: 1, dateStart: dateRange.start, dateEnd: dateRange.end }"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="app in items"
						:key="app._id"
						:to="`/backoffice/applications/${app._id}`"
						placeholder="file"
						:title="app.profile?.name || app.name || 'Application'"
						:subtitle="formatDate(app.createdAt)"
						classImage="flex-none w-3r h-3r radius-small bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					/>
				</Feed>
			</Block>

			<Block title="Recent Customers">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:states="{ empty: { title: 'No Customers', description: 'No customers.' } }"
					:store="{ read: (options) => customers.read(options), state: customers.state }"
					:options="{ sortParam: 'createdAt', sortOrder: 'desc', limit: 5, page: 1, dateStart: dateRange.start, dateEnd: dateRange.end }"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="customer in items"
						:key="customer._id"
						:to="`/backoffice/customers/${customer._id}`"
						:photo="customer.avatar || customer.profile?.photo"
						placeholder="profile"
						:title="customer.profile?.name || customer.name || customer.phone || 'Customer'"
						:subtitle="formatDate(customer.createdAt)"
						classImage="flex-none w-3r h-3r radius-extra bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					/>
				</Feed>
			</Block>
		</div>

		<!-- 7. Popular Products — FULL WIDTH -->
		<Block v-if="isModuleInstalled('products')" title="Popular Products" class="mn-b-medium">
			<div v-if="orderStats.popularProducts?.length" class="cols-5 tablet:cols-3 mobile:cols-2 gap-small">
				<CardProduct
					v-for="product in orderStats.popularProducts"
					:key="product._id"
					:product="product"
					:showAddToCart="false"
					class="bg-base"
				>
					<p class="t-secondary t-small mn-t-thin">{{ product.totalQuantity }} sold · ₽{{ product.totalRevenue?.toLocaleString() }}</p>
				</CardProduct>
			</div>
			<p v-else class="opacity-50">No product data</p>
		</Block>

		<!-- 8. Recent Registrations + 9. Upcoming Rents -->
		<div v-if="isModuleInstalled('auth') || isModuleInstalled('rents')" class="mn-b-medium cols-2 mobile:cols-1 gap-small">
			<Block v-if="isModuleInstalled('auth')" title="Recent Registrations">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:states="{ empty: { title: 'No Registrations', description: 'No recent registrations.' } }"
					:store="{ read: (options) => users.actions.read(options), state: users.state }"
					:options="{ sortParam: 'createdAt', sortOrder: 'desc', limit: 5, page: 1, dateStart: dateRange.start, dateEnd: dateRange.end }"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="user in items"
						:key="user._id"
						:to="`/users/${user._id}`"
						:photo="user.avatar || user.profile?.photo"
						placeholder="profile"
						:title="user.profile?.name || user.username || user.email || 'User'"
						:subtitle="formatDate(user.createdAt)"
						classImage="flex-none w-3r h-3r radius-extra bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					/>
				</Feed>
			</Block>

			<Block v-if="isModuleInstalled('rents')" title="Upcoming Rents">
				<Feed
					:showLoadMore="false"
					:LoadMore="false"
					:search="false"
					:states="{ empty: { title: 'No Rents', description: 'No upcoming bookings.' } }"
					:store="{ read: (options) => rents.actions.read(options), state: rents.state }"
					:options="{ sortParam: 'startDate', sortOrder: 'desc', limit: 5 }"
					v-slot="{ items }"
					class="flex flex-column gap-thin"
				>
					<Card
						v-for="rent in items"
						:key="rent._id"
						:to="`/backoffice/orders/${rent.order}`"
						placeholder="calendar"
						:title="rent.product?.name || 'Rent'"
						:subtitle="`${formatDate(rent.startDate)} — ${formatDate(rent.endDate)}`"
						classImage="flex-none w-3r h-3r radius-small bg-accent-transp-10 flex flex-center o-hidden"
						classContent="flex-child o-hidden"
						class="bg-base pd-small radius-small hover-scale-1 transition-default flex flex-v-center gap-small"
					/>
				</Feed>
			</Block>
		</div>
	</div>
	</div>
</template>

<script setup>
import { ref, computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'

import Block from '@martyrs/src/design-system/components/Block/Block.vue'
import Chart from '@martyrs/src/design-system/components/Chart/Chart.vue'
import Feed from '@martyrs/src/design-system/components/Feed/Feed.vue'
import Table from '@martyrs/src/design-system/components/Table/Table.vue'
import Dropdown from '@martyrs/src/design-system/components/Dropdown/Dropdown.vue'
import Calendar from '@martyrs/src/design-system/components/Calendar/Calendar.vue'
import Card from '@martyrs/src/modules/core/views/components/blocks/Card.vue'
import CardStat from '@martyrs/src/modules/core/views/components/blocks/CardStat.vue'
import CardProduct from '@martyrs/src/modules/products/views/components/blocks/CardProduct.vue'
import SectionPageTitle from '@martyrs/src/modules/core/views/components/sections/SectionPageTitle.vue'

import Icon from '@martyrs/src/design-system/components/Icon/Icon.vue'

import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'
import { orders } from '@martyrs/src/modules/orders/store/orders.js'
import { events } from '@martyrs/src/modules/events/store/events.js'
import { rents } from '@martyrs/src/modules/rents/views/store/rents.store.js'
import applications from '@martyrs/src/modules/orders/store/applications.js'
import customers from '@martyrs/src/modules/orders/store/customers.store.js'
import { users } from '@martyrs/src/modules/auth/views/store/users.js'

const route = useRoute()
const { formatDate, isModuleInstalled } = useGlobalMixins()

const orderStats = ref({})
const userStats = ref({})
// Default: last 7 days (today inclusive), UTC to match Calendar
const now = new Date()
const todayUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
const weekAgoUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 6))
const dateRange = ref({
	start: weekAgoUTC.toISOString(),
	end: new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)).toISOString()
})

// Calendar may return strings, timestamps, or Date objects
function toDate(v) {
	if (v instanceof Date) return v
	return new Date(v)
}

const dateRangeLabel = computed(() => {
	if (!dateRange.value?.start || !dateRange.value?.end) return 'All time'
	const s = toDate(dateRange.value.start)
	const e = toDate(dateRange.value.end)
	const sKey = toDateStr(s)
	const eKey = toDateStr(e)
	if (sKey === eKey) return formatDate(sKey, { dayMonth: true })
	return formatDate(sKey, { dayMonth: true }) + ' — ' + formatDate(eKey, { dayMonth: true })
})


// Order table columns
const orderColumns = [
	{ key: '_id', label: 'Order' },
	{ key: 'positions', label: 'Items' },
	{ key: 'status', label: 'Status' },
	{ key: 'createdAt', label: 'Date' }
]

function formatChartValue(val) {
	if (val >= 1000) return '₽' + Math.round(val / 1000) + 'k'
	return '₽' + Math.round(val)
}

function toDateStr(d) {
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function fillDays(revenueMap, startDate, endDate) {
	const result = []
	const s = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()))
	const e = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()))
	while (s <= e && result.length < 3650) {
		const key = toDateStr(s)
		result.push({ _id: key, revenue: revenueMap[key] || 0 })
		s.setUTCDate(s.getUTCDate() + 1)
	}
	return result
}

const revenueChart = computed(() => {
	const raw = orderStats.value.dailyRevenue
	if (!raw?.length) return []

	// Build a map from date string to revenue
	const revenueMap = {}
	for (const d of raw) revenueMap[d._id] = d.revenue

	const start = dateRange.value?.start
	const end = dateRange.value?.end

	if (start && end) {
		return fillDays(revenueMap, toDate(start), toDate(end))
	}

	// All time: from project start to today
	return fillDays(revenueMap, new Date(Date.UTC(2022, 9, 17)), todayUTC)
})

// Formatters
function formatId(id) {
	return id ? `${id.slice(0, 4)}...${id.slice(-4)}` : ''
}

function formatXDate(dateStr) {
	if (!dateStr) return ''
	return formatDate(dateStr, { dayMonth: true })
}

function getStatusClass(status) {
	const classes = {
		pending: 'bg-accent-secondary-transp-10 t-accent-secondary',
		processing: 'bg-accent-transp-10 t-accent',
		created: 'bg-accent-transp-10 t-accent',
		confirmed: 'bg-positive-transp-10 t-positive',
		completed: 'bg-positive-transp-10 t-positive',
		canceled: 'bg-negative-transp-10 t-negative'
	}
	return classes[status] || 'bg-card t-secondary'
}

function getEventStatus(date) {
	if (!date?.start) return 'No date'
	const now = new Date()
	const start = new Date(date.start)
	const end = date.end ? new Date(date.end) : start
	if (now > end) return 'Finished'
	if (now >= start && now <= end) return 'Live'
	const diffDays = Math.ceil((start - now) / (1000 * 60 * 60 * 24))
	if (diffDays <= 1) return 'Tomorrow'
	if (diffDays <= 7) return `In ${diffDays} days`
	return 'Upcoming'
}

function getEventStatusClass(date) {
	const status = getEventStatus(date)
	if (status === 'Finished') return 'bg-card t-secondary'
	if (status === 'Live') return 'bg-positive-transp-10 t-positive'
	if (status === 'Tomorrow') return 'bg-accent-secondary-transp-10 t-accent-secondary'
	return 'bg-accent-transp-10 t-accent'
}

watchEffect(async () => {
	const params = {}
	if (dateRange.value?.start) params.startDate = new Date(dateRange.value.start).toISOString()
	if (dateRange.value?.end) params.endDate = new Date(dateRange.value.end).toISOString()
	const promises = []
	if (isModuleInstalled('orders')) promises.push($axios.get('/api/orders/stats', { params }))
	if (isModuleInstalled('auth')) promises.push($axios.get('/api/users/stats', { params }))
	const results = await Promise.all(promises)
	let idx = 0
	if (isModuleInstalled('orders')) orderStats.value = results[idx++].data
	if (isModuleInstalled('auth')) userStats.value = results[idx++].data
})
</script>
